import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Takedown from "@/pages/Takedown";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { TermsPage, PrivacyPage } from "@/pages/LegalPages";
import FeaturesPage from "@/pages/FeaturesPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import HindiToEnglishPage from "@/pages/HindiToEnglishPage";
import AutomaticSubtitleTranslatorPage from "@/pages/AutomaticSubtitleTranslatorPage";
import FaqsPage from "@/pages/FaqsPage";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSpendPage from "@/pages/AdminSpendPage";
import AdminDiagnosticsPage from "@/pages/AdminDiagnosticsPage";
import AdminSupportPage from "@/pages/AdminSupportPage";
import SupportPage from "@/pages/SupportPage";
import AdminSetup from "@/pages/AdminSetup";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Read hash from useLocation to be reactive
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ImpersonationBanner />
      <Routes>
        <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/spend" element={<ProtectedRoute><AdminSpendPage /></ProtectedRoute>} />
        <Route path="/admin/diagnostics" element={<ProtectedRoute><AdminDiagnosticsPage /></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute><AdminSupportPage /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        <Route path="/support/tickets/:ticketId" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        <Route path="/admin/setup" element={<AdminSetup />} />
        <Route path="/takedown" element={<Takedown />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/hindi-to-english-subtitle-translator" element={<HindiToEnglishPage />} />
        <Route path="/automatic-subtitle-translator" element={<AutomaticSubtitleTranslatorPage />} />
        <Route path="/faqs" element={<FaqsPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster theme="dark" position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}
